import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { inject } from '@angular/core';
import { Observable, from, map, switchMap } from 'rxjs';
import { UserAuthService } from './UserAuth.service';
import { msalSettled } from './auth/msal-settle';
import { rememberIntendedUrl } from './auth/return-to';
import { RolesOutcome, RolesResolverService } from './auth/roles-resolver.service';
import { landingPathFor, loadPendingLink } from './auth/pending-link';
import { ExternalIdSignInService } from './auth/external-id-sign-in.service';

/**
 * THE ACCESS DECISION, unchanged. Exported so it can be tested without the
 * asynchronous wait wrapped around it.
 */
export function decideAccess(
  authService: UserAuthService,
  router: Router,
  /**
   * The URL the user actually asked for. Optional so existing callers and tests
   * keep working; without it the customer detour simply has nothing to return to,
   * which is the behaviour that shipped.
   */
  requestedUrl?: string,
  /**
   * Whether the roles read below have been ESTABLISHED. Defaults to 'known' so
   * existing callers and tests keep their meaning.
   */
  rolesOutcome: RolesOutcome = 'known'
): boolean {
  if (!authService.isAuthenticated()) {
    router.navigate(['/login']);
    return false;
  }

  // ROLES MUST BE ESTABLISHED BEFORE THEY ARE READ. An empty role list used to
  // fall through to `return true` as though the user needed no company, and the
  // layout then called GetProjectlist without one. See RolesResolverService.
  if (rolesOutcome === 'denied') {
    // AN INVITED USER IS "DENIED" UNTIL THE INVITE IS ACCEPTED: CheckUserExists
    // knows nothing of them yet. With a link pending, send them back to it,
    // where accepting is the next step, not to a dead end.
    const pending = loadPendingLink();
    router.navigate([pending ? landingPathFor(pending.kind) : '/unauthorized']);
    return false;
  }
  // NO ACCOUNT ON AN AUTHENTICATED SESSION IS NOT "NO COMPANY NEEDED". It used to fall through to
  // `return true` with no roles, which is the state the resolver exists to close: the app loads and
  // data calls go out with no company. It cannot be answered, so it is not allowed.
  if (rolesOutcome === 'no-account') {
    rememberIntendedUrl(requestedUrl);
    router.navigate(['/login']);
    return false;
  }
  if (rolesOutcome === 'unavailable') {
    // No verdict. /login re-asks and offers the retry control; it never guesses.
    rememberIntendedUrl(requestedUrl);
    router.navigate(['/login']);
    return false;
  }

  const roles = authService.getRoles();
  const needsCustomerSelection =
    roles.includes('SuperAdmin') || roles.includes('administrator');

  if (needsCustomerSelection && !authService.getActiveCustomer()) {
    // REMEMBER WHERE THEY WERE GOING. Deep-linking /admin/drums in a fresh tab
    // correctly detours through the customer picker — but without this the
    // destination was discarded and the picker dropped them at /projects, so a
    // shared link only worked for someone who already had a customer selected.
    //
    // Same mechanism as the reload-restore detour, deliberately: one
    // implementation of "where was the user going", validated in one place.
    rememberIntendedUrl(requestedUrl);
    router.navigate(['/select-customer']);
    return false;
  }

  return true;
}

/**
 * WAIT FOR MSAL BEFORE DECIDING. This is the fix for the account-switch reload
 * storm, and the wait is the entire fix.
 *
 * WHAT THE STORM ACTUALLY WAS, measured rather than reasoned about. Returning
 * from Microsoft, MSAL caches the URL that initiated the login (ORIGIN_URI) and,
 * because `navigateToLoginRequestUrl` defaults to true, navigates back to it and
 * then checks "am I on that URL yet?" before processing the response hash.
 *
 * This guard used to answer synchronously, at +21..24ms into every load — BEFORE
 * MSAL had finished handling the redirect. `isAuthenticated()` was therefore
 * still false (getAllAccounts() is empty until the redirect promise resolves),
 * so the guard pushed /login. MSAL then reached its comparison, saw /login
 * instead of the project URL it was returning to, concluded it had NOT arrived,
 * and navigated again. That is the loop:
 *
 *     t0        +0ms   href=/temperature?...&project=X   MATCHES ORIGIN_URI
 *     pushState +24ms  href=/login                       <- THIS GUARD
 *     (MSAL compares, mismatches, navigates back)        -> next iteration
 *
 * 99 full document loads at ~55ms apart, no backoff, ended only by MSAL's 30s
 * redirectNavigationTimeout resetting the request cache. Every one of the 99
 * navigations came from MSAL's own navigateInternal — the app never navigated.
 *
 * WHY IT WAS ROUTE-DEPENDENT, which is what made it look like data corruption:
 * /admin/customers is behind a lazy-loaded module, so this guard did not run
 * until +141ms — by which time MSAL had settled, the comparison matched, and the
 * loop exited after one navigation. The eagerly-routed project URL ran the guard
 * at +24ms and lost the race every single time. Same guard, same build,
 * different chunk-load timing. It is NOT coupled to the Cosmos data: it
 * reproduced with the dead tenant rows deleted, ad7c7fc deployed, and the stale
 * stored selection cleared.
 *
 * NOT FIXED BY `navigateToLoginRequestUrl: false`. That would stop the visible
 * storm by suppressing the return navigation, leaving whatever re-enters login
 * on that route doing it silently on every project navigation. The inequality is
 * the bug; this makes the return navigation land and settle.
 *
 * The wait is bounded (MSAL_SETTLE_TIMEOUT_MS) and costs nothing once settled —
 * `inProgress$` replays its current value. A genuinely unauthenticated user
 * still gets bounced to /login, just after MSAL has had its say rather than
 * before.
 */
export const userAuthGuard: CanActivateFn = (
  _route,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const authService = inject(UserAuthService);
  const broadcast = inject(MsalBroadcastService);
  const router = inject(Router);
  const rolesResolver = inject(RolesResolverService);
  const externalId = inject(ExternalIdSignInService);

  // THE SAME WAIT, FOR THE OTHER MSAL INSTANCE. A CIAM session reaches isAuthenticated() only when
  // ExternalIdSignInService.restore() resolves (initialize, read its sessionStorage cache, emit),
  // and nothing else orders that before this guard. On a reload deep in the app the guard could
  // otherwise decide "signed out" and send a CIAM user to /login (sme-vericure-webui, cd44e7f).
  // restore() is idempotent and a no-op when CIAM is unconfigured.
  const settled = Promise.all([
    msalSettled(broadcast, 'userAuthGuard'),
    externalId.enabled() ? externalId.restore().catch(() => null) : Promise.resolve(null),
  ]);

  return from(settled).pipe(
    // Only an authenticated session has roles to establish. The resolver
    // answers 'known' without a request once they are held.
    switchMap(() =>
      authService.isAuthenticated()
        ? from(rolesResolver.ensureRoles())
        : from(Promise.resolve<RolesOutcome>('known'))
    ),
    map((outcome) => decideAccess(authService, router, state.url, outcome))
  );
};
